import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ClockClockwise, Phone, Buildings, FloppyDisk, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const STATUS_COLORS = {
  answered: "bg-green-100 text-green-800",
  completed: "bg-green-100 text-green-800",
  no_answer: "bg-yellow-100 text-yellow-800",
  busy: "bg-red-100 text-red-800",
  voicemail: "bg-blue-100 text-blue-800",
  callback: "bg-purple-100 text-purple-800",
  ringing: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  ended: "bg-gray-100 text-gray-700",
  calling: "bg-yellow-100 text-yellow-800",
};

const OUTCOME_COLORS = {
  interested: "bg-green-100 text-green-800",
  vacancy_identified: "bg-emerald-100 text-emerald-800",
  new_opportunity: "bg-teal-100 text-teal-800",
  callback: "bg-blue-100 text-blue-800",
  not_interested: "bg-gray-100 text-gray-600",
  no_answer: "bg-yellow-100 text-yellow-700",
  voicemail: "bg-yellow-50 text-yellow-700",
  busy: "bg-orange-100 text-orange-700",
  wrong_number: "bg-red-100 text-red-700",
  do_not_contact: "bg-red-200 text-red-900",
  existing_client: "bg-purple-100 text-purple-800",
};

function EditableNotes({ callId, initialNotes }) {
  const [notes, setNotes] = useState(initialNotes);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/calls/${callId}/notes`, { notes });
      setSaved(true);
      setEditing(false);
      toast.success("Notes saved");
      setTimeout(() => setSaved(false), 2000);
    } catch { toast.error("Failed to save notes"); }
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="space-y-1" data-testid={`notes-edit-${callId}`}>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="text-xs min-h-[60px] resize-y" autoFocus onKeyDown={e => { if (e.key === "Escape") { setEditing(false); setNotes(initialNotes); } }} />
        <div className="flex gap-1">
          <button onClick={save} disabled={saving} className="text-[10px] text-emerald-700 font-medium flex items-center gap-0.5 hover:underline" data-testid={`notes-save-${callId}`}>
            <FloppyDisk size={10} /> {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={() => { setEditing(false); setNotes(initialNotes); }} className="text-[10px] text-[#9CA3AF] hover:underline ml-2">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="group cursor-pointer" onClick={() => setEditing(true)} title="Click to edit" data-testid={`notes-display-${callId}`}>
      {notes ? (
        <div className="text-xs text-[#374151] whitespace-pre-wrap break-words leading-relaxed">{notes}</div>
      ) : (
        <span className="text-xs text-[#D1D5DB] italic">Click to add notes</span>
      )}
      {saved && <span className="text-[10px] text-emerald-600 flex items-center gap-0.5 mt-0.5"><Check size={10} /> Saved</span>}
    </div>
  );
}

const PAGE_SIZE = 50;

export default function CallLogsPage() {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/calls", { params: { page, limit: PAGE_SIZE } });
        setCalls(data.items || []);
        setTotal(data.total || 0);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [page]);

  const formatDuration = (s) => {
    if (!s) return "0:00";
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  };

  return (
    <div data-testid="call-logs-page" className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-[#0F172A]">Call Activity Log</h1>
        <p className="text-sm text-[#6B7280]">{total} calls recorded</p>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bm-table-header">
                <TableHead className="text-xs">Date / Time</TableHead>
                <TableHead className="text-xs">Recruiter</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Contact</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Number</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Outcome</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Duration</TableHead>
                <TableHead className="text-xs hidden xl:table-cell">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => <TableRow key={i}><TableCell colSpan={8}><div className="h-8 skeleton-shimmer rounded" /></TableCell></TableRow>)
              ) : calls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <ClockClockwise size={40} className="mx-auto text-[#D1D5DB] mb-3" />
                    <p className="text-sm text-[#6B7280]">No call logs yet. Start dialling to see call history.</p>
                  </TableCell>
                </TableRow>
              ) : calls.map((c) => (
                <TableRow key={c.id} className="hover:bg-[#F9FAFB] align-top" data-testid={`call-row-${c.id}`}>
                  <TableCell className="text-sm text-[#0F172A]">
                    {c.created_at ? new Date(c.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                  </TableCell>
                  <TableCell className="text-sm text-[#374151]">
                    <div>{c.user_name || "-"}</div>
                    {c.extension && <span className="text-[10px] text-[#9CA3AF]">Ext. {c.extension}</span>}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="text-sm text-[#0F172A]">{c.contact_name || "-"}</div>
                    {c.company_id && <span className="text-[10px] text-[#6B7280] flex items-center gap-1"><Buildings size={10} /> Company</span>}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-[#6B7280] font-mono">{c.phone_number || "-"}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${STATUS_COLORS[c.call_status] || "bg-gray-100"}`}>
                      {c.call_status?.replace("_", " ") || "-"}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {c.outcome ? (
                      <Badge className={`text-[10px] ${OUTCOME_COLORS[c.outcome] || "bg-gray-100"}`}>
                        {c.outcome.replace("_", " ")}
                      </Badge>
                    ) : "-"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-[#6B7280] font-mono">{formatDuration(c.duration)}</TableCell>
                  <TableCell className="hidden xl:table-cell min-w-[220px] max-w-[350px]">
                    <EditableNotes callId={c.id} initialNotes={c.notes || ""} />
                    {c.mr_banks_analysis && (
                      <details className="mt-1 text-xs" data-testid={`mr-banks-analysis-${c.id}`}>
                        <summary className="cursor-pointer font-semibold text-[#C5A059]">Mr Banks{c.mr_banks_analysis.average ? ` · ${c.mr_banks_analysis.average}/10` : ""}{c.mr_banks_analysis.outcome ? ` · ${c.mr_banks_analysis.outcome}` : ""}</summary>
                        <div className="mt-1 space-y-0.5 text-[#374151]">
                          {c.mr_banks_analysis.summary && <div>{c.mr_banks_analysis.summary}</div>}
                          {c.mr_banks_analysis.objections?.length > 0 && <div><b>Objections:</b> {c.mr_banks_analysis.objections.map(o => o.type || o).join(", ")}</div>}
                          {c.mr_banks_analysis.buying_signals?.length > 0 && <div><b>Buying signals:</b> {c.mr_banks_analysis.buying_signals.join(", ")}</div>}
                          {c.mr_banks_analysis.follow_up && <div><b>Follow-up:</b> {c.mr_banks_analysis.follow_up}</div>}
                          {c.mr_banks_analysis.coaching_tip && <div><b>Coaching tip:</b> {c.mr_banks_analysis.coaching_tip}</div>}
                        </div>
                      </details>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!loading && total > PAGE_SIZE && (
          <div className="px-4 pb-3">
            <Pagination page={page} total={total} limit={PAGE_SIZE} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
